/**
 * miraCMS 2.0 — Typed API-Client.
 * Vite dev: relative `/api/...` URLs werden zu http://127.0.0.1:8080 proxied
 * (siehe vite.config.ts). Prod: VITE_API_BASE setzt die Base-URL.
 */
import type {
  ProjectBranding,
  ProjectsListResponse,
  ProjectFieldsResponse,
  SavePayload,
  SaveResponse,
  RebuildResponse,
  ThemeToken,
  ThemeContrastPair,
  AuthLoginResponse,
  AuthLoginTotpResponse,
  AuthMeResponse,
  AuthLogoutResponse,
  PageHistoryResponse,
  PageDiffResponse,
  PageRestoreResponse,
  StagingShareRequest,
  StagingShareResponse,
  StagingShareError,
  GateTextsResponse,
  GateTextMap,
  LiveBackupsResponse,
  LiveRollbackResponse,
  PromotePreviewResponse,
  PromoteResponse,
  TotpSetupResponse,
  CollectionsListResponse,
  CollectionItemsResponse,
  CollectionSchemaResponse,
  CollectionItemResponse,
  CollectionFeldwert,
  CategoriesResponse,
  UploadResponse,
  MedienAntwort,
  AnfragenAntwort,
  Markenprofil,
  MedienLoeschAntwort,
  PreviewStatusResponse,
  SeiteNeuAntwort,
  AltPruefEintrag,
  BausteinKatalog,
  Menue,
  Vorabpruefung,
  Seitenplan,
  SeitenplanAktion,
  SeitenplanAktionAntwort,
  ClaudeSchluesselInfo,
  ClaudeAenderung,
  AssistentVerfuegbar,
  AssistentGespraech,
  AssistentEinstellungen,
  KiBildStand,
  Stockfoto,
} from '@/types/api'
import type {
  AnnotationsResponse,
  AnnotationResponse,
  AnnotationNeu,
  AnnotationPatch,
} from '@/types/annotation'
import type { GastLink, GastZugang, ModulAntwort } from '@/types/gast'
import type {
  Abnahme,
  Auftrag,
  DomainStand,
  Einrichtung,
  Gestaltung,
  Kundenzugang,
  NeueWebsiteEingabe,
  WerkbankStand,
} from '@/types/werkbank'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      /* Bei FormData KEINEN eigenen Content-Type setzen: Den schreibt der
         Browser selbst und hängt die `boundary` an, ohne die der Server den
         Rumpf nicht zerlegen kann. Ein eigener Kopf macht jeden Datei-Upload
         still kaputt (16.08.2026). */
      ...(init.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
    ...init,
  })
  const text = await res.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }
  if (!res.ok) {
    throw new ApiError(`HTTP ${res.status} on ${path}`, res.status, body)
  }
  return body as T
}

export const api = {
  /**
   * Protokoll lesen. Der Server filtert bereits auf das, was die Sitzung sehen
   * darf — ein Kunde bekommt nur sein Projekt, projektlose Ereignisse
   * (Anmeldung, Benutzerverwaltung) nur die Verwaltung.
   */
  auditList(opts: {
    project?: string
    actor?: string
    days?: number
    limit?: number
    offset?: number
  } = {}): Promise<AuditResponse> {
    const p = new URLSearchParams()
    if (opts.project) p.set('project', opts.project)
    if (opts.actor) p.set('actor', opts.actor)
    if (opts.days) p.set('days', String(opts.days))
    if (opts.limit) p.set('limit', String(opts.limit))
    if (opts.offset) p.set('offset', String(opts.offset))
    const qs = p.toString()
    return request<AuditResponse>(`/api/v1/audit${qs ? `?${qs}` : ''}`)
  },

  listProjects(): Promise<ProjectsListResponse> {
    return request<ProjectsListResponse>('/api/v1/projects')
  },

  /**
   * Öffentliches Branding (pre-login) für die gebrandete Login-Maske, dazu
   * die Sprache, in der die Maske spricht: die Hauptsprache des Projekts.
   */
  publicBranding(id: string): Promise<{ branding: ProjectBranding & { sprache?: 'de' | 'en' } }> {
    return request(`/api/v1/auth/branding/${encodeURIComponent(id)}`)
  },

  projectFields(
    id: string,
    options?: { site?: string; lang?: string },
  ): Promise<ProjectFieldsResponse> {
    const params = new URLSearchParams()
    if (options?.site) params.set('site', options.site)
    if (options?.lang) params.set('lang', options.lang)
    const qs = params.toString()
    return request<ProjectFieldsResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/fields${qs ? `?${qs}` : ''}`,
    )
  },

  saveProject(id: string, payload: SavePayload): Promise<SaveResponse> {
    return request<SaveResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/save`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ).catch((err: unknown) => {
      if (err instanceof ApiError && (err.status === 422 || err.status === 400)) {
        return err.body as SaveResponse
      }
      throw err
    })
  },

  // ── Phase C: Collections (Produkt-/Katalog-Pflege) ─────────────────────

  listCollections(id: string): Promise<CollectionsListResponse> {
    return request<CollectionsListResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections`,
    )
  },

  collectionItems(id: string, c: string): Promise<CollectionItemsResponse> {
    return request<CollectionItemsResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}`,
    )
  },

  collectionSchema(id: string, c: string): Promise<CollectionSchemaResponse> {
    return request<CollectionSchemaResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/schema`,
    )
  },

  collectionItem(id: string, c: string, itemId: string): Promise<CollectionItemResponse> {
    return request<CollectionItemResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/items/${encodeURIComponent(itemId)}`,
    )
  },

  createCollectionItem(id: string, c: string, data: Record<string, CollectionFeldwert>): Promise<{ created: boolean; id: string }> {
    return request<{ created: boolean; id: string }>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/items`,
      { method: 'POST', body: JSON.stringify({ data }) },
    )
  },

  updateCollectionItem(id: string, c: string, itemId: string, data: Record<string, CollectionFeldwert>): Promise<{ updated: boolean; id: string }> {
    return request<{ updated: boolean; id: string }>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/items/${encodeURIComponent(itemId)}`,
      { method: 'PUT', body: JSON.stringify({ data }) },
    )
  },

  deleteCollectionItem(id: string, c: string, itemId: string): Promise<{ deleted: boolean }> {
    return request<{ deleted: boolean }>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/items/${encodeURIComponent(itemId)}`,
      { method: 'DELETE' },
    )
  },

  reorderCollection(id: string, c: string, ids: string[]): Promise<{ reordered: boolean; count: number }> {
    return request<{ reordered: boolean; count: number }>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/reorder`,
      { method: 'POST', body: JSON.stringify({ ids }) },
    )
  },

  // ── Kategorie-Verwaltung (enum des Gruppen-Felds) ──────────────────────

  collectionCategories(id: string, c: string): Promise<CategoriesResponse> {
    return request<CategoriesResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/categories`,
    )
  },

  addCategory(id: string, c: string, value: string): Promise<{ added: boolean; values: string[] }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/categories`,
      { method: 'POST', body: JSON.stringify({ value }) },
    )
  },

  renameCategory(id: string, c: string, from: string, to: string): Promise<{ renamed: boolean; values: string[]; items_updated: number }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/categories`,
      { method: 'PUT', body: JSON.stringify({ from, to }) },
    )
  },

  deleteCategory(id: string, c: string, value: string, reassignTo?: string): Promise<{ deleted: boolean; values: string[]; items_updated: number }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/categories`,
      { method: 'DELETE', body: JSON.stringify({ value, reassign_to: reassignTo ?? '' }) },
    )
  },

  reorderCategories(id: string, c: string, order: string[]): Promise<{ reordered: boolean; values: string[] }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/categories/reorder`,
      { method: 'POST', body: JSON.stringify({ order }) },
    )
  },

  /** Bild einer Kategorie setzen (leerer image-String = Bild entfernen). */
  setCategoryImage(id: string, c: string, value: string, image: string): Promise<{ updated: boolean; image: string | null }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/collections/${encodeURIComponent(c)}/categories/image`,
      { method: 'PUT', body: JSON.stringify({ value, image }) },
    )
  },

  // ── Veröffentlichen (gebündeltes commit+push+build) ────────────────────

  /** Gibt es nicht veröffentlichte (uncommittete) Katalog-Änderungen? */
  publishStatus(id: string): Promise<{ dirty: boolean }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/publish/status`)
  },

  /** Alle aufgeschobenen Änderungen veröffentlichen. 409 = läuft schon. */
  /**
   * `live` schickt der Server nur, wenn die Aenderung wirklich auf die
   * oeffentliche Website ging (Kunden-Kiosk). Bei Admin und Redakteurin geht
   * sie in die Vorschau, und genau das muss die Meldung auch sagen.
   */
  publish(id: string): Promise<{ published: boolean; committed: boolean; built: boolean; live?: boolean; error: string | null }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/publish`, { method: 'POST' })
  },

  /** Stockfotos suchen (Pexels, Pixabay); `bereit` nennt die Quellen mit Schlüssel. */
  stockSuche(
    projekt: string,
    p: { quelle: string; q: string; ausrichtung?: string },
  ): Promise<{ quelle: string; treffer: Stockfoto[]; bereit: string[] }> {
    const qs = new URLSearchParams({ quelle: p.quelle, q: p.q, ausrichtung: p.ausrichtung ?? '' })
    return request(`/api/v1/projects/${encodeURIComponent(projekt)}/stockfotos?${qs.toString()}`)
  },

  /** Ein Stockfoto ins Projekt übernehmen; zurück kommt der Feldwert wie beim Upload. */
  stockUebernehmen(projekt: string, p: { quelle: string; id: string }): Promise<{ uploaded: boolean; value: string }> {
    return request(`/api/v1/projects/${encodeURIComponent(projekt)}/stockfotos`, {
      method: 'POST',
      body: JSON.stringify(p),
    })
  },

  /** KI-Bilder: eingerichtet? und wie viele es diesen Monat schon gab. */
  kiBildStand(projekt: string): Promise<KiBildStand> {
    return request(`/api/v1/projects/${encodeURIComponent(projekt)}/ki-bild`)
  },

  /** Ein KI-Bild erzeugen; es liegt danach schon im Projekt, zurück kommt der Feldwert. */
  kiBildErzeugen(
    projekt: string,
    p: { beschreibung: string; format: 'quer' | 'hoch' | 'quadrat' },
  ): Promise<KiBildStand & { value: string; width: number; height: number }> {
    return request(`/api/v1/projects/${encodeURIComponent(projekt)}/ki-bild`, {
      method: 'POST',
      body: JSON.stringify(p),
    })
  },

  /** Schlüssel der Bildquellen: nur ob einer da ist und wie er endet. */
  bildquellen(): Promise<{ quellen: Record<string, { hat_schluessel: boolean; endet_auf: string | null }> }> {
    return request('/api/v1/bildquellen/einstellungen')
  },

  bildquelleSetzen(
    p: { quelle: string; schluessel: string },
  ): Promise<{ quellen: Record<string, { hat_schluessel: boolean; endet_auf: string | null }> }> {
    return request('/api/v1/bildquellen/einstellungen', { method: 'PUT', body: JSON.stringify(p) })
  },

  /**
   * Bild-Upload (multipart). KEIN Content-Type-Header setzen — der Browser
   * setzt multipart/form-data inkl. boundary selbst. Server konvertiert zu
   * WebP + verkleinert + committet, gibt den Feld-Wert-Pfad zurück.
   */
  uploadImage(
    id: string,
    file: File,
    opts?: { subdir?: string; defer?: boolean; hintergrund?: boolean },
  ): Promise<UploadResponse> {
    const form = new FormData()
    form.append('file', file)
    if (opts?.subdir) form.append('subdir', opts.subdir)
    // defer: Bild nur ablegen, Commit/Build kommt beim Veröffentlichen.
    if (opts?.defer) form.append('defer', '1')
    // hintergrund: committen, aber nicht auf den Bau warten (Bild im Chat, 02.11.2026).
    if (opts?.hintergrund) form.append('hintergrund', '1')
    return fetch(`${API_BASE}/api/v1/projects/${encodeURIComponent(id)}/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      body: form,
    }).then(async (res) => {
      const text = await res.text()
      let body: unknown = null
      if (text) {
        try {
          body = JSON.parse(text)
        } catch {
          body = text
        }
      }
      if (!res.ok) {
        throw new ApiError(`HTTP ${res.status} on upload`, res.status, body)
      }
      return body as UploadResponse
    })
  },

  /**
   * Datei-Upload (PDF) zu Cloudflare R2. Wie uploadImage multipart, aber das
   * Backend legt die Datei auf R2 ab und gibt die öffentliche cdn-URL zurück.
   */
  /** Video für den Einstieg zu R2 (03.11.2026, K8): MP4 oder WebM, höchstens 25 MB. */
  uploadVideo(id: string, file: File): Promise<{ uploaded: boolean; url: string }> {
    const form = new FormData()
    form.append('file', file)
    return fetch(`${API_BASE}/api/v1/projects/${encodeURIComponent(id)}/upload-video`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      body: form,
    }).then(async (res) => {
      const text = await res.text()
      let body: unknown = null
      try {
        body = text ? JSON.parse(text) : null
      } catch {
        body = null
      }
      if (!res.ok) throw new ApiError(`HTTP ${res.status} on upload-video`, res.status, body)
      return body as { uploaded: boolean; url: string }
    })
  },

  uploadFile(id: string, file: File): Promise<{ uploaded: boolean; url: string; thumbnail: string | null }> {
    const form = new FormData()
    form.append('file', file)
    return fetch(`${API_BASE}/api/v1/projects/${encodeURIComponent(id)}/upload-file`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      body: form,
    }).then(async (res) => {
      const text = await res.text()
      let body: unknown = null
      if (text) {
        try {
          body = JSON.parse(text)
        } catch {
          body = text
        }
      }
      if (!res.ok) {
        throw new ApiError(`HTTP ${res.status} on upload-file`, res.status, body)
      }
      return body as { uploaded: boolean; url: string; thumbnail: string | null }
    })
  },

  /**
   * URL zum Anzeigen eines im Repo liegenden Bildes (Editor-Vorschau).
   * value ist der gespeicherte Feld-Wert, z.B. "/src/assets/uploads/x.webp".
   */
  mediaUrl(id: string, value: string): string {
    const rel = value.replace(/^\/+/, '')
    return `${API_BASE}/api/v1/projects/${encodeURIComponent(id)}/media/${rel}`
  },

  /** Die Mediathek: jedes Bild des Projekts, mit Größe und wo es steht. */
  medien(id: string): Promise<MedienAntwort> {
    return request<MedienAntwort>(`/api/v1/projects/${encodeURIComponent(id)}/medien`)
  },

  /**
   * Ein im CMS hochgeladenes Bild löschen, das nirgends mehr steht. Der
   * Server prüft beides noch einmal und antwortet sonst mit 409.
   */
  medienLoeschen(id: string, pfad: string): Promise<MedienLoeschAntwort> {
    return request<MedienLoeschAntwort>(
      `/api/v1/projects/${encodeURIComponent(id)}/medien?pfad=${encodeURIComponent(pfad)}`,
      { method: 'DELETE' },
    )
  },

  /** Eine Sprache nachträglich ergänzen (03.11.2026, K6): Texte der Hauptsprache als Ausgangspunkt, „in Arbeit“. */
  spracheErgaenzen(id: string, site: string, lang: string): Promise<{ ok: true; kopiert: number }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/sprachen`, { method: 'POST', body: JSON.stringify({ site, lang }) })
  },

  spracheFreigeben(id: string, lang: string): Promise<{ ok: true }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/sprachen/${encodeURIComponent(lang)}/freigeben`, { method: 'POST' })
  },

  /** Markenprofil der Website (03.11.2026, K29). */
  markenprofil(id: string): Promise<{ profil: Markenprofil }> {
    return request<{ profil: Markenprofil }>(`/api/v1/projects/${encodeURIComponent(id)}/markenprofil`)
  },

  markenprofilSpeichern(id: string, profil: Markenprofil): Promise<{ profil: Markenprofil }> {
    return request<{ profil: Markenprofil }>(`/api/v1/projects/${encodeURIComponent(id)}/markenprofil`, {
      method: 'PUT',
      body: JSON.stringify(profil),
    })
  },

  /** Formular-Anfragen einer Website bei Cloudflare (K11), neueste zuerst. */
  anfragen(id: string): Promise<AnfragenAntwort> {
    return request<AnfragenAntwort>(`/api/v1/projects/${encodeURIComponent(id)}/anfragen`)
  },

  anfrageLoeschen(id: string, anfrage: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/v1/projects/${encodeURIComponent(id)}/anfragen/${encodeURIComponent(anfrage)}`,
      { method: 'DELETE' },
    )
  },

  // ── Site-Theme (Marken-Farben, Phase D) ── Admin-only (Server erzwingt).
  getTheme(id: string): Promise<{ tokens: ThemeToken[]; pairs: ThemeContrastPair[]; gestaltung?: Gestaltung }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/theme`)
  },
  saveTheme(
    id: string,
    values: Record<string, string>,
  ): Promise<{ ok: boolean; tokens: ThemeToken[] }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/theme`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    })
  },

  // ── Texte der Vorschau-Sperrseite ── global (nicht pro Projekt), admin-only.
  getGateTexts(): Promise<GateTextsResponse> {
    return request<GateTextsResponse>('/api/v1/staging/gate-texts')
  },
  saveGateTexts(texts: GateTextMap): Promise<GateTextsResponse & { ok: boolean }> {
    return request('/api/v1/staging/gate-texts', {
      method: 'PUT',
      body: JSON.stringify({ texts }),
    })
  },

  /**
   * Billige Auskunft: Laeuft gerade ein Bau, und wie alt ist die Vorschau?
   * Damit wartet das Panel auf den Bau, den das Speichern ohnehin angestossen
   * hat, statt einen zweiten anzuwerfen.
   */
  previewStatus(id: string): Promise<PreviewStatusResponse> {
    return request<PreviewStatusResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/preview-status`,
    )
  },

  rebuildPreview(
    id: string,
    opts: { site?: string; lang?: string; page?: string },
  ): Promise<RebuildResponse> {
    return request<RebuildResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/preview/rebuild`,
      {
        method: 'POST',
        body: JSON.stringify(opts),
      },
    ).catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 422) {
        return err.body as RebuildResponse
      }
      throw err
    })
  },

  /**
   * Struktur einer Doku-Seite ändern: Absatz einfügen, verschieben, löschen.
   * Danach stimmt das Block-Verzeichnis nicht mehr — der Aufrufer baut die
   * Vorschau neu und lädt die Feldliste.
   */
  docsBlockAktion(
    id: string,
    opts: {
      site: string
      page: string
      lang: string
      block: string
      aktion: 'davor' | 'danach' | 'hoch' | 'runter' | 'loeschen'
      text?: string
    },
  ): Promise<{ ok: true; aktion: string; block: string }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/docs/block`, {
      method: 'POST',
      body: JSON.stringify(opts),
    })
  },

  /** Bausteinkatalog des Projekts, null wenn es kein Baukasten-Projekt ist. */
  bausteine(id: string): Promise<{ katalog: BausteinKatalog | null; vorlagen?: string[] }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/bausteine`)
  },

  /** Seitenplan einer Baukasten-Seite: welche Abschnitte in welcher Reihenfolge. */
  seitenplan(id: string, seite: string): Promise<{ seite: string; plan: Seitenplan }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/seitenplan/${encodeURIComponent(seite)}`)
  },

  /**
   * Aufbau einer Baukasten-Seite ändern. Der Server prüft, schreibt Plan und
   * Texte aller Sprachen in einem Commit und stösst den Vorschau-Bau an.
   */
  seitenplanAktion(
    id: string,
    seite: string,
    site: string,
    aktion: SeitenplanAktion,
  ): Promise<SeitenplanAktionAntwort> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/seitenplan/${encodeURIComponent(seite)}/aktion`, {
      method: 'POST',
      body: JSON.stringify({ site, ...aktion }),
    })
  },

  /** Neue Seite aus Bausteinen, als Entwurf. Die Adresse bildet der Server aus dem Titel. */
  baukastenSeiteAnlegen(
    id: string,
    site: string,
    titel: string,
    vorlage = '',
  ): Promise<{ ok: true; seite: string; plan: Seitenplan; git: SeitenplanAktionAntwort['git'] }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/seitenplaene`, {
      method: 'POST',
      body: JSON.stringify(vorlage ? { site, titel, vorlage } : { site, titel }),
    })
  },

  /** Eine Baukasten-Seite kopieren, als Entwurf unter neuer Adresse (31.10.2026). */
  baukastenSeiteDuplizieren(
    id: string,
    site: string,
    seite: string,
    titel: string,
  ): Promise<{ ok: true; seite: string; plan: Seitenplan; git: SeitenplanAktionAntwort['git'] }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/seitenplan/${encodeURIComponent(seite)}/duplizieren`, {
      method: 'POST',
      body: JSON.stringify({ site, titel }),
    })
  },

  /**
   * Adresse einer im CMS entstandenen Seite ändern. Der Server bildet sie aus
   * dem Wunsch, zieht Plan, Texte und Menüplatz um und leitet die alte Adresse
   * weiter, wenn die Seite schon sichtbar war.
   */
  seiteAdresse(
    id: string,
    seite: string,
    site: string,
    adresse: string,
  ): Promise<{ ok: true; seite: string; git: SeitenplanAktionAntwort['git'] }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/seitenplan/${encodeURIComponent(seite)}/adresse`, {
      method: 'POST',
      body: JSON.stringify({ site, adresse }),
    })
  },

  /**
   * Einen eigenen Umbau rückgängig machen oder einen Rückgängig-Commit
   * wiederholen (Cmd+Z, Cmd+Shift+Z). Der Server nimmt nur Commits der
   * anfragenden Person aus dem CMS, und nur ohne Konflikt.
   */
  rueckgaengig(id: string, hash: string): Promise<{ ok: true; rueckgaengig: string; git: SeitenplanAktionAntwort['git'] }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/rueckgaengig`, {
      method: 'POST',
      body: JSON.stringify({ hash }),
    })
  },

  /** Das Menü der Website: welche Seiten oben und unten stehen. */
  menue(id: string): Promise<Menue> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/navigation`)
  },

  /** Das Menü setzen. Ein Commit auf staging, der Server prüft jede Seite. */
  menueSetzen(
    id: string,
    site: string,
    menue: { kopf: string[]; fuss: string[]; unter?: Record<string, string[]> },
  ): Promise<{ ok: true; kopf: string[]; fuss: string[]; unter?: Record<string, string[]>; git: SeitenplanAktionAntwort['git'] }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/navigation`, {
      method: 'PUT',
      body: JSON.stringify({ site, ...menue }),
    })
  },

  /** Eine im CMS entstandene Seite entfernen, samt Texten aller Sprachen. */
  baukastenSeiteEntfernen(
    id: string,
    seite: string,
    site: string,
  ): Promise<{ ok: true; seite: string; git: SeitenplanAktionAntwort['git'] }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/seitenplan/${encodeURIComponent(seite)}?site=${encodeURIComponent(site)}`,
      { method: 'DELETE' },
    )
  },

  /**
   * Neue Seite aus einer Vorlage (bei UnderWave: ein Artikel).
   *
   * Danach gibt es eine Seite mehr: Der Aufrufer laedt die Feldliste neu und
   * baut die Vorschau. Der Slug entsteht im Server, nicht hier — er entscheidet
   * ueber eine Adresse, und die Pruefung auf Doppelte braucht den Bestand.
   */
  seiteAnlegen(
    id: string,
    opts: { vorlage: string; titel: string; lang: string },
  ): Promise<SeiteNeuAntwort> {
    return request<SeiteNeuAntwort>(`/api/v1/projects/${encodeURIComponent(id)}/seiten`, {
      method: 'POST',
      body: JSON.stringify(opts),
    })
  },

  /** Entwurf umschalten oder eine zweite Sprache ergaenzen. */
  seiteAendern(
    id: string,
    key: string,
    opts: { vorlage: string; entwurf?: boolean; lang?: string; titel?: string },
  ): Promise<{ ok: true; key: string }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/seiten/${encodeURIComponent(key)}`,
      { method: 'PATCH', body: JSON.stringify(opts) },
    )
  },

  /**
   * Seite ganz entfernen.
   *
   * Einen Entwurf darf jeder mit Schreibrecht, etwas Sichtbares nur Admin —
   * der Server prüft es, hier steht es nur als Hinweis.
   */
  seiteEntfernen(id: string, key: string, vorlage: string): Promise<{ ok: true; key: string }> {
    return request(
      `/api/v1/projects/${encodeURIComponent(id)}/seiten/${encodeURIComponent(key)}`
        + `?vorlage=${encodeURIComponent(vorlage)}`,
      { method: 'DELETE' },
    )
  },

  authLogin(username: string, password: string): Promise<AuthLoginResponse> {
    return request<AuthLoginResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }).catch((err: unknown) => {
      if (err instanceof ApiError && (err.status === 401 || err.status === 429)) {
        return err.body as AuthLoginResponse
      }
      throw err
    })
  },

  authLoginTotp(pendingToken: string, code: string): Promise<AuthLoginTotpResponse> {
    return request<AuthLoginTotpResponse>('/api/v1/auth/login/totp', {
      method: 'POST',
      body: JSON.stringify({ pending_token: pendingToken, code }),
    }).catch((err: unknown) => {
      if (err instanceof ApiError && (err.status === 401 || err.status === 429)) {
        return err.body as AuthLoginTotpResponse
      }
      throw err
    })
  },

  totpSetup(currentPassword: string): Promise<TotpSetupResponse> {
    return request<TotpSetupResponse>('/api/v1/account/totp/setup', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword }),
    })
  },

  totpEnable(code: string): Promise<{ ok: true; totp_enabled: true }> {
    return request<{ ok: true; totp_enabled: true }>('/api/v1/account/totp/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    })
  },

  totpDisable(currentPassword: string): Promise<{ ok: true; totp_enabled: false }> {
    return request<{ ok: true; totp_enabled: false }>('/api/v1/account/totp/disable', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword }),
    })
  },

  authMe(): Promise<AuthMeResponse> {
    return request<AuthMeResponse>('/api/v1/auth/me')
  },

  authLogout(): Promise<AuthLogoutResponse> {
    return request<AuthLogoutResponse>('/api/v1/auth/logout', {
      method: 'POST',
    })
  },

  pageHistory(
    id: string,
    opts: { site: string; lang: string; page: string },
  ): Promise<PageHistoryResponse> {
    const params = new URLSearchParams(opts)
    return request<PageHistoryResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/history?${params.toString()}`,
    )
  },

  pageDiff(
    id: string,
    commit: string,
    opts: { site: string; lang: string; page: string },
  ): Promise<PageDiffResponse> {
    const params = new URLSearchParams(opts)
    return request<PageDiffResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/history/${encodeURIComponent(commit)}/diff?${params.toString()}`,
    )
  },

  pageRestore(
    id: string,
    commit: string,
    opts: { site: string; lang: string; page: string },
  ): Promise<PageRestoreResponse> {
    const params = new URLSearchParams(opts)
    return request<PageRestoreResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/history/${encodeURIComponent(commit)}/restore?${params.toString()}`,
      { method: 'POST' },
    ).catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 400) {
        return err.body as PageRestoreResponse
      }
      throw err
    })
  },

  stagingShare(
    id: string,
    payload: StagingShareRequest,
  ): Promise<StagingShareResponse | StagingShareError> {
    return request<StagingShareResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/staging/share`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ).catch((err: unknown) => {
      // 500 nur mit eigenem Code (schluessel_nicht_angelegt), sonst ist es ein
      // echter Absturz und geht an fehlersatz().
      if (err instanceof ApiError && (err.status === 400 || err.status === 422
        || (err.status === 500 && (err.body as { error?: unknown } | null)?.error === 'schluessel_nicht_angelegt'))) {
        return err.body as StagingShareError
      }
      throw err
    })
  },

  /** Was vor „Auf Live“ noch zu tun ist: Platzhalter, Bildbeschreibungen, Grundregeln. */
  vorabpruefung(id: string): Promise<Vorabpruefung> {
    return request<Vorabpruefung>(`/api/v1/projects/${encodeURIComponent(id)}/vorabpruefung`)
  },

  promotePreview(id: string): Promise<PromotePreviewResponse> {
    return request<PromotePreviewResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/promote/preview`,
    )
  },

  promote(id: string): Promise<PromoteResponse> {
    return request<PromoteResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/promote`,
      { method: 'POST' },
    ).catch((err: unknown) => {
      // 409 und 403 kommen bei Websites bei Cloudflare vor dem Bau (Ausbau H):
      // Es fehlt etwas für den ersten Livegang, oder der Code ist nötig.
      if (err instanceof ApiError && [400, 403, 409, 422, 502, 503].includes(err.status)) {
        return err.body as PromoteResponse
      }
      throw err
    })
  },

  liveBackups(id: string): Promise<LiveBackupsResponse> {
    return request<LiveBackupsResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/live/backups`,
    )
  },

  liveRollback(id: string, stand?: string): Promise<LiveRollbackResponse> {
    return request<LiveRollbackResponse>(
      `/api/v1/projects/${encodeURIComponent(id)}/live/rollback`,
      { method: 'POST', body: JSON.stringify(stand ? { stand } : {}) },
    ).catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 502) {
        return err.body as LiveRollbackResponse
      }
      throw err
    })
  },

  // ── Phase 5: Account + Users (Settings) ────────────────────────────────

  /**
   * Ein hochgeladenes Dokument endgueltig entfernen.
   *
   * Der Server weigert sich, solange die Adresse im Projekt noch irgendwo
   * steht: Zwei Eintraege mit derselben PDF teilen sich dieselbe Datei.
   */
  dateiLoeschen(projectId: string, url: string): Promise<{ deleted: true; cache_geleert: boolean }> {
    return request<{ deleted: true; cache_geleert: boolean }>(
      `/api/v1/projects/${encodeURIComponent(projectId)}/upload-file/loeschen`,
      { method: 'POST', body: JSON.stringify({ url }) },
    )
  },

  /** Die Sprache der Oberflaeche beim Zugang merken, nicht nur im Browser. */
  spracheSetzen(sprache: 'de' | 'en'): Promise<{ ok: true }> {
    return request<{ ok: true }>('/api/v1/account/sprache', {
      method: 'POST',
      body: JSON.stringify({ sprache }),
    })
  },

  changeOwnPassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
    return request<{ ok: true }>('/api/v1/account/password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    })
  },

  listUsers(): Promise<{ users: UserSummary[] }> {
    return request<{ users: UserSummary[] }>('/api/v1/users')
  },

  createUser(payload: {
    username: string
    name?: string
    password: string
    role: 'admin' | 'editor' | 'client'
    projects?: string[]
  }): Promise<{ user: UserSummary }> {
    return request<{ user: UserSummary }>('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  updateUser(
    username: string,
    payload: { name?: string; role?: string; projects?: string[]; konto_rechte?: string[] },
  ): Promise<{ user: UserSummary }> {
    return request<{ user: UserSummary }>(
      `/api/v1/users/${encodeURIComponent(username)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    )
  },

  resetUserPassword(username: string, newPassword: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/v1/users/${encodeURIComponent(username)}/password`,
      { method: 'POST', body: JSON.stringify({ new_password: newPassword }) },
    )
  },

  /** Löscht den Zugang und beendet dabei dessen offene Sitzungen. */
  deleteUser(username: string): Promise<{ ok: true; beendete_sitzungen?: number }> {
    return request<{ ok: true; beendete_sitzungen?: number }>(
      `/api/v1/users/${encodeURIComponent(username)}`,
      { method: 'DELETE' },
    )
  },

  // ----- Notizen an der Vorschau (Welle 4d, Abschnitt B) --------------------

  /**
   * Alle Notizen eines Projekts. Ohne Filter kommt alles — gefiltert wird im
   * Panel, damit der Zähler offener Notizen stimmt, auch wenn die Liste gerade
   * nur die erledigten zeigt.
   */
  annotations(project: string, opts: { page?: string; lang?: string } = {}): Promise<AnnotationsResponse> {
    const p = new URLSearchParams()
    if (opts.page) p.set('page', opts.page)
    if (opts.lang) p.set('lang', opts.lang)
    const qs = p.toString()
    return request<AnnotationsResponse>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations${qs ? `?${qs}` : ''}`,
    )
  },

  /**
   * „Gibt es etwas Neues?" — die billige Frage fuer den Abgleich im Takt.
   * Antwort ist ein kurzer Fingerabdruck; aendert er sich, lohnt die Liste.
   */
  annotationsStand(project: string): Promise<{ stand: string }> {
    return request<{ stand: string }>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations/stand`,
    )
  },

  annotationCreate(project: string, payload: AnnotationNeu): Promise<AnnotationResponse> {
    return request<AnnotationResponse>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations`,
      { method: 'POST', body: JSON.stringify(payload) },
    )
  },

  annotationUpdate(project: string, id: string, payload: AnnotationPatch): Promise<AnnotationResponse> {
    return request<AnnotationResponse>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    )
  },

  annotationReply(project: string, id: string, text: string): Promise<AnnotationResponse> {
    return request<AnnotationResponse>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations/${encodeURIComponent(id)}/replies`,
      { method: 'POST', body: JSON.stringify({ text }) },
    )
  },

  annotationDelete(project: string, id: string): Promise<{ ok: true }> {
    return request<{ ok: true }>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    )
  },

  /**
   * Eine Datei an einen Kommentar hängen (16.08.2026).
   *
   * Bewusst kein `Content-Type`: Bei `FormData` setzt ihn der Browser selbst
   * und hängt die `boundary` an, ohne die der Server den Rumpf nicht zerlegen
   * kann. Ein eigener Kopf hier macht den Upload kaputt.
   */
  annotationAnhangHoch(project: string, id: string, datei: File): Promise<AnnotationResponse> {
    const body = new FormData()
    body.append('datei', datei)
    return request<AnnotationResponse>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations/${encodeURIComponent(id)}/anhaenge`,
      { method: 'POST', body },
    )
  },

  annotationAnhangWeg(project: string, id: string, anhang: string): Promise<AnnotationResponse> {
    return request<AnnotationResponse>(
      `/api/v1/projects/${encodeURIComponent(project)}/annotations/${encodeURIComponent(id)}`
        + `/anhaenge/${encodeURIComponent(anhang)}`,
      { method: 'DELETE' },
    )
  },

  /** Die Adresse, unter der eine angehängte Datei zu sehen ist. */
  anhangUrl(project: string, id: string, anhang: string): string {
    return `/api/v1/projects/${encodeURIComponent(project)}/annotations/${encodeURIComponent(id)}`
      + `/anhaenge/${encodeURIComponent(anhang)}`
  },

  // ----- Kommentar-Link für Kunden (Welle 4d, Abschnitt D) -----------------

  /**
   * Den Link einlösen. Der einzige Aufruf, der ohne Anmeldung geht.
   *
   * Der Token steht im Body, nicht im Pfad: Pfade landen in Server-Logs und
   * in Verlaufslisten, ein Schlüssel hat dort nichts verloren.
   */
  gastEinloesen(token: string): Promise<GastZugang> {
    return request<GastZugang>('/api/v1/auth/gast', {
      method: 'POST',
      body: JSON.stringify({ token }),
    })
  },

  /** Welche Wähler-Stellungen dieses Projekt je Rolle zeigt (4d.10). */
  module(project: string): Promise<ModulAntwort> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/module`)
  },

  /** `modi: null` nimmt die Einstellung zurück, die Rolle fällt auf ihren Standard. */
  moduleSetzen(project: string, rolle: string, modi: string[] | null): Promise<ModulAntwort> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/module`, {
      method: 'PUT',
      body: JSON.stringify({ rolle, modi }),
    })
  },

  /** Rechte des Projekts setzen (heute: `ki`, Claude darf hier arbeiten). Nur Admin. */
  projektRechteSetzen(project: string, rechte: string[]): Promise<{ projekt_rechte: string[] }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/module`, {
      method: 'PUT',
      body: JSON.stringify({ projekt_rechte: rechte }),
    })
  },

  /** Eigene Claude-Schlüssel und die Adresse des MCP-Anschlusses. */
  claudeMeine(): Promise<{ schluessel: ClaudeSchluesselInfo[]; adresse: string; aus: boolean }> {
    return request('/api/v1/account/claude')
  },

  /** Neuen Claude-Schlüssel erzeugen. Der Klartext steht nur in dieser Antwort. */
  claudeNeu(name: string): Promise<{ id: string; schluessel: string; adresse: string }> {
    return request('/api/v1/account/claude', { method: 'POST', body: JSON.stringify({ name }) })
  },

  claudeAbschalten(id: string): Promise<{ ok: true }> {
    return request(`/api/v1/account/claude/${encodeURIComponent(id)}`, { method: 'DELETE' })
  },

  /** Notschalter für Claude. */
  kiSchalter(): Promise<{ aus: boolean; seit: string | null; von: string | null }> {
    return request('/api/v1/ki/schalter')
  },

  kiSchalterSetzen(aus: boolean): Promise<{ aus: boolean; seit: string | null; von: string | null }> {
    return request('/api/v1/ki/schalter', { method: 'PUT', body: JSON.stringify({ aus }) })
  },

  claudeLetzte(project: string): Promise<{ letzte: ClaudeAenderung | null }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/claude/letzte`)
  },

  claudeZuruecknehmen(project: string): Promise<{ ok: true; zurueckgenommen: string }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/claude/zuruecknehmen`, { method: 'POST' })
  },

  // ── Assistent im Panel ──────────────────────────────────────────

  assistentVerfuegbar(project: string): Promise<AssistentVerfuegbar> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/assistent`)
  },

  /** Nachricht an den Assistenten. Antwortet sofort; den Stand holt `assistentStand`. */
  assistentSenden(
    project: string,
    body: { nachricht: string; gespraech?: string; site: string; lang: string; seite: string; bilder?: string[] },
  ): Promise<{ gespraech: string }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/assistent`, { method: 'POST', body: JSON.stringify(body) })
  },

  assistentStand(project: string, gespraech: string): Promise<AssistentGespraech> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/assistent/${encodeURIComponent(gespraech)}`)
  },

  assistentEinstellungen(): Promise<AssistentEinstellungen> {
    return request('/api/v1/assistent/einstellungen')
  },

  assistentEinstellungenSetzen(body: {
    schluessel?: string | null
    modell?: string
    grenze_je_projekt?: number
    grenze_gesamt?: number
  }): Promise<AssistentEinstellungen> {
    return request('/api/v1/assistent/einstellungen', { method: 'PUT', body: JSON.stringify(body) })
  },

  /** Bildbeschreibungen, die nach einem Bildtausch zu prüfen sind. */
  altPruefen(project: string, site: string, lang: string): Promise<{ eintraege: AltPruefEintrag[] }> {
    const q = new URLSearchParams({ site, lang })
    return request(`/api/v1/projects/${encodeURIComponent(project)}/alt-pruefen?${q.toString()}`)
  },

  /** „Passt so“: Die Beschreibung bleibt, für genau dieses Bild. */
  altPruefenErledigt(
    project: string,
    ziel: { site: string; lang: string; page: string; alt: string; bild: string },
  ): Promise<{ ok: true }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/alt-pruefen/erledigt`, {
      method: 'POST',
      body: JSON.stringify(ziel),
    })
  },

  gastLink(project: string): Promise<{ link: GastLink | null }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/gast-link`)
  },

  /** Neuer Link — der alte wird damit ungültig. */
  gastLinkNeu(project: string): Promise<{ link: GastLink }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/gast-link`, {
      method: 'POST',
    })
  },

  gastLinkSchalten(project: string, active: boolean): Promise<{ link: GastLink | null }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/gast-link`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    })
  },

  // ── Neue Website, Domain, Einrichtung (Ausbau H, 28.10.2026) ──

  /** Heikle Schritte mit dem Code aus der App bestätigen; gilt 10 Minuten. */
  bestaetigen(code: string): Promise<{ ok: true; bis: number }> {
    return request('/api/v1/account/bestaetigen', { method: 'POST', body: JSON.stringify({ code }) })
  },

  werkbankStand(): Promise<WerkbankStand> {
    return request<WerkbankStand>('/api/v1/werkbank')
  },

  websiteAnlegen(eingabe: NeueWebsiteEingabe): Promise<{ auftrag: Auftrag }> {
    return request('/api/v1/websites', { method: 'POST', body: JSON.stringify(eingabe) })
  },

  auftrag(id: string): Promise<{ auftrag: Auftrag }> {
    return request(`/api/v1/auftraege/${encodeURIComponent(id)}`)
  },

  /** Nach einem Fehler in der Nacharbeit am selben Schritt weiter. */
  auftragWiederholen(id: string): Promise<{ auftrag: Auftrag }> {
    return request(`/api/v1/auftraege/${encodeURIComponent(id)}/wiederholen`, { method: 'POST' })
  },

  domainStand(project: string): Promise<{ domain: DomainStand | null; auftrag: Auftrag | null; ausserhalb?: boolean }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/domain`)
  },

  domainVorbereiten(project: string, domain: string, haupt: 'apex' | 'www'): Promise<{ auftrag: Auftrag }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/domain`, {
      method: 'POST',
      body: JSON.stringify({ domain, haupt }),
    })
  },

  einrichtung(project: string): Promise<Einrichtung> {
    return request<Einrichtung>(`/api/v1/projects/${encodeURIComponent(project)}/einrichtung`)
  },

  formularEmpfaenger(project: string, email: string): Promise<{ formular: Einrichtung['formular'] }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/formular`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  empfaengerBestaetigen(token: string): Promise<{ ok: true; name: string }> {
    return request('/api/v1/auth/empfaenger-bestaetigen', { method: 'POST', body: JSON.stringify({ token }) })
  },

  abnahme(project: string): Promise<{ abnahme: Abnahme | null; gefragt: boolean }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/abnahme`)
  },

  freigeben(project: string, name: string): Promise<{ abnahme: Abnahme }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/abnahme`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    })
  },

  kundenzugaenge(project: string): Promise<{ zugaenge: Kundenzugang[] }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/kundenzugang`)
  },

  kundenzugangAnlegen(project: string, benutzername: string, name: string): Promise<{ benutzername: string; passwort: string }> {
    return request(`/api/v1/projects/${encodeURIComponent(project)}/kundenzugang`, {
      method: 'POST',
      body: JSON.stringify({ benutzername, name }),
    })
  },

  /** Schrift und Ecken; Farben gehen weiter über `saveTheme`. */
  saveGestaltung(
    id: string,
    wahl: { schrift?: string; ecken?: string; schrift_ueberschrift?: string; stil?: string; einstellungen?: Record<string, string> },
  ): Promise<{ ok: boolean; tokens: ThemeToken[]; gestaltung: Gestaltung }> {
    return request(`/api/v1/projects/${encodeURIComponent(id)}/theme`, {
      method: 'PUT',
      body: JSON.stringify(wahl),
    })
  },
}

export type AuditEntry = {
  ts: string
  actor_type: string
  actor_name: string
  ip: string
  project: string
  action: string
  resource_type: string
  resource_id: string
  outcome: 'ok' | 'denied' | 'failed' | string
}

export type AuditResponse = {
  entries: AuditEntry[]
  total: number
  chainOk: boolean
  chainBrokenAt: number | null
  retentionMonths: number
  retentionMonthsRights: number
}

export type UserSummary = {
  username: string
  name: string
  role: 'admin' | 'editor' | 'client' | string
  projects: string[] | null
  totp_enabled?: boolean
  /** Rechte am Konto (Ausbau H): websites_anlegen, domains_verbinden, kunden_zugang. */
  konto_rechte?: string[]
}

export { ApiError }
